<script setup>
import { ref, watch, computed, onMounted, nextTick, onBeforeMount } from 'vue';
import { useStore } from 'vuex';
import Textarea from '../../../../../components-next/textarea/TextArea.vue';
import Button from '../../../../../components-next/button/Button.vue';
import ValidationFeedback from '../shared/ValidationFeedback.vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  tagLabel: {
    type: String,
    default: 'Tag',
  },
  tagPlaceholder: {
    type: String,
    default: 'Selecione uma tag',
  },
  descriptionLabel: {
    type: String,
    default: 'Contexto de Uso da Tag',
  },
  descriptionPlaceholder: {
    type: String,
    default: 'Digite como o agente deve usar esta tag especificamente neste contexto',
  },
});

const emit = defineEmits(['update', 'validation']);

const store = useStore();
const localItems = ref([...props.items]);
// Array para armazenar quais campos foram tocados
const touchedItems = ref([]);
// Array para controlar estado dos dropdowns
const dropdownStates = ref([]);

// Array de títulos de tags que devem ser excluídas
// Adicione aqui os títulos das tags que não devem aparecer
const EXCLUDED_TAG_TITLES = [
  'audio', 'atendimento-humano'
];

// Buscar todas as tags disponíveis
onBeforeMount(() => {
  store.dispatch('labels/get');
});

// Computed para pegar as tags do store (excluindo as tags bloqueadas)
const availableLabels = computed(() => {
  const allLabels = store.getters['labels/getLabels'] || [];
  // Filtrar tags excluídas baseado no título
  return allLabels.filter(label => 
    !EXCLUDED_TAG_TITLES.includes(label.title)
  );
});

// Computed para tags já selecionadas (para evitar duplicatas)
const selectedTagIds = computed(() => {
  return localItems.value.map(item => item.tagId).filter(Boolean);
});

// Computed para opções do select (excluindo tags já selecionadas)
const tagOptions = computed(() => {
  return availableLabels.value
    .filter(label => !selectedTagIds.value.includes(label.id))
    .map(label => ({
      value: label.id,
      label: label.title,
      color: label.color,
      description: label.description
    }));
});

// Computed para verificar se todos os itens são válidos
const areAllItemsValid = computed(() => {
  // Se não houver itens, é válido (campos são opcionais)
  if (localItems.value.length === 0) return true;
  
  // Se houver itens, todos devem ter tag e descrição preenchidos
  return localItems.value.every(item => {
    return item.tagId && item.description && item.description.trim();
  });
});

// Emite o estado de validação sempre que muda
watch(areAllItemsValid, (newValue) => {
  emit('validation', newValue);
});

// Emite o estado inicial na montagem
onMounted(() => {
  // Inicializa localItems com tagError e descriptionError
  localItems.value.forEach(item => {
    if (!item.hasOwnProperty('tagError')) item.tagError = '';
    if (!item.hasOwnProperty('descriptionError')) item.descriptionError = '';
  });
  
  // Inicializa estados dos dropdowns
  dropdownStates.value = new Array(localItems.value.length).fill(false);
  
  // Atualiza o estado de validação
  emit('validation', areAllItemsValid.value);
});

const removeItem = async (index) => {
  localItems.value.splice(index, 1);
  touchedItems.value.splice(index, 1);
  dropdownStates.value.splice(index, 1);
  updateItems();
  
  // Aguarda o próximo ciclo de renderização antes de validar
  await nextTick();
  
  // Força a revalidação após remoção
  emit('validation', areAllItemsValid.value);
};

const handleBlur = (index, field) => {
  // Marca o campo como tocado
  if (!touchedItems.value[index]) {
    touchedItems.value[index] = { tagId: false, description: false };
  }
  
  touchedItems.value[index][field] = true;
  
  // Valida o campo
  validateField(index, field);
};

const validateField = (index, field) => {
  const item = localItems.value[index];
  
  // Só valida se o campo já foi tocado
  if (!touchedItems.value[index] || !touchedItems.value[index][field]) {
    return;
  }
  
  if (field === 'tagId' && !item.tagId) {
    item.tagError = 'Seleção de tag é obrigatória';
  } else {
    item.tagError = '';
  }
  
  if (field === 'description' && !item.description?.trim()) {
    item.descriptionError = 'Contexto de Uso é obrigatório';
  } else {
    item.descriptionError = '';
  }
  
  // Emite atualização após validação
  updateItems();
};

const updateItems = () => {
  emit('update', [...localItems.value]);
};

// Função para obter o nome da tag pelo ID
const getTagName = (tagId) => {
  const tag = availableLabels.value.find(label => label.id === tagId);
  return tag ? tag.title : '';
};

// Função para obter a descrição padrão da tag pelo ID
const getTagDefaultDescription = (tagId) => {
  const tag = availableLabels.value.find(label => label.id === tagId);
  return tag ? tag.description : '';
};


// Watch para sincronizar com as props
watch(() => props.items, (newItems) => {
  if (JSON.stringify(newItems) !== JSON.stringify(localItems.value)) {
    localItems.value = [...newItems];
    
    // Inicializa as flags de erro para cada item
    localItems.value.forEach(item => {
      if (!item.hasOwnProperty('tagError')) item.tagError = '';
      if (!item.hasOwnProperty('descriptionError')) item.descriptionError = '';
    });
    
    // Reset touched status for new items
    touchedItems.value = new Array(newItems.length).fill().map(() => ({ tagId: false, description: false }));
    
    // Reset dropdown states for new items
    dropdownStates.value = new Array(newItems.length).fill(false);
    
    // Emite o estado de validação ao receber novos itens
    emit('validation', areAllItemsValid.value);
  }
}, { deep: true });

// Handler para mudança de tag
const handleTagChange = (index, tagId) => {
  localItems.value[index].tagId = tagId;
  
  // Fecha o dropdown
  dropdownStates.value[index] = false;
  
  // Se o campo foi tocado, valida
  if (touchedItems.value[index]?.tagId) {
    validateField(index, 'tagId');
  }
  
  updateItems();
};

// Controle do dropdown
const toggleDropdown = (index) => {
  // Fecha todos os outros dropdowns
  dropdownStates.value.forEach((_, i) => {
    if (i !== index) {
      dropdownStates.value[i] = false;
    }
  });

  // Toggle do dropdown atual
  dropdownStates.value[index] = !dropdownStates.value[index];
};

const closeDropdown = (index) => {
  dropdownStates.value[index] = false;
};

// Função para obter o texto exibido no botão do dropdown
const getSelectedTagLabel = (tagId) => {
  if (!tagId) return props.tagPlaceholder;
  const tag = availableLabels.value.find(label => label.id === tagId);
  return tag ? tag.title : props.tagPlaceholder;
};
</script>

<template>
  <div class="space-y-4">
    <TransitionGroup name="list-fade" tag="div">
      <div 
        v-for="(item, index) in localItems" 
        :key="`tags-data-field-${index}`" 
        class="relative bg-n-slate-2 dark:bg-n-slate-12/5 p-4 rounded-lg border border-n-slate-5 dark:border-n-slate-10/20 mt-7 tag-field-item dynamic-field-container"
      >
        <!-- Cabeçalho -->
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ localItems.length > 1 ? `Tag ${localItems.length - index}` : 'Tag' }}
          </h4>
          <Button
            icon="i-lucide-trash-2"
            variant="faded"
            color="ruby"
            size="xs"
            class="remove-button-rotate"
            @click="removeItem(index)"
            v-tooltip.top="'Remover tag'"
          />
        </div>
        
        <!-- Grid de campos -->
        <div class="space-y-3">
          <!-- Seleção de Tag -->
          <div>
            <label class="text-xs text-n-slate-11 mb-1 block">{{ tagLabel }} *</label>
            <div class="relative w-full custom-dropdown" v-on-clickaway="() => closeDropdown(index)">
              <!-- Botão do dropdown -->
              <button 
                @click="toggleDropdown(index)" 
                @blur="handleBlur(index, 'tagId')"
                type="button"
                :class="[
                  'flex items-center justify-between px-3 h-[38px] text-sm border rounded-lg transition-colors cursor-pointer w-full',
                  'bg-n-slate-1 border-n-slate-6 text-n-slate-12 hover:border-primary-600 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 focus:outline-none',
                  'dark:bg-n-slate-1 dark:border-n-slate-6 dark:text-n-slate-12 dark:hover:border-primary-600 dark:focus:border-primary-600 dark:focus:ring-primary-600/20',
                  { 'border-n-ruby-8': item.tagError }
                ]"
              >
                <span :class="{ 'text-n-slate-9': !item.tagId }">{{ getSelectedTagLabel(item.tagId) }}</span>
                <span 
                  class="i-lucide-chevron-down ml-2 h-4 w-4 text-n-slate-10 transition-transform"
                  :class="{ 'rotate-180': dropdownStates[index] }"
                ></span>
              </button>

              <!-- Lista de opções -->
              <div
                v-if="dropdownStates[index]"
                class="absolute top-full mt-1 left-0 w-full bg-n-slate-1 border border-n-slate-6 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                style="z-index: 50;"
              >
                <button 
                  v-for="option in tagOptions" 
                  :key="option.value" 
                  @click="handleTagChange(index, option.value)" 
                  type="button"
                  class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-3 transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
                  :class="{ 'bg-n-slate-3': item.tagId === option.value }"
                >
                  <span 
                    class="w-3 h-3 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: option.color || '#e5e7eb' }"
                  ></span>
                  <span class="truncate">{{ option.label }}</span>
                </button>
                
                <div v-if="tagOptions.length === 0" class="px-3 py-2 text-sm text-n-slate-9 text-center">
                  Nenhuma tag disponível
                </div>
              </div>
            </div>
            
            <!-- Exibir erro de validação -->
            <div v-if="item.tagError" class="mt-1 text-xs text-n-ruby-11">
              {{ item.tagError }}
            </div>
          </div>
          
          <!-- Descrição Padrão da Tag (somente leitura) -->
          <div v-if="item.tagId">
            <label class="text-xs text-n-slate-11 mb-1 flex items-center gap-1">
              Descrição Padrão da Tag
              <span 
                class="i-lucide-info w-3 h-3 text-n-slate-9"
                v-tooltip.top="`A descrição padrão da tag vem das configurações do sistema e não pode ser editada aqui. Para editar a descrição padrão, acesse Configurações → Etiquetas.`"
              ></span>
            </label>
            <div class="min-h-[2.5rem] px-3 py-2 text-sm bg-n-slate-1 border border-n-slate-5 rounded-lg text-n-slate-11">
              <span v-if="getTagDefaultDescription(item.tagId)" class="whitespace-pre-wrap">{{ getTagDefaultDescription(item.tagId) }}</span>
                <span v-else class="italic text-n-slate-9">Descrição não foi definida para esta tag, acesse Configurações → Etiquetas para editar.</span>
            </div>
          </div>

          <!-- Descrição Personalizada - Usando Textarea -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="text-xs text-n-slate-11">{{ descriptionLabel }} *</label>
              <span v-if="item.description" class="text-xs text-n-slate-10">{{ item.description.length }}</span>
            </div>
            <Textarea
              v-model="item.description"
              :placeholder="descriptionPlaceholder"
              :error="item.descriptionError"
              :rows="2"
              :autoHeight="true"
              :resize="true"
              :minHeight="'2.5rem'"
              :maxHeight="'8rem'"
              @blur="handleBlur(index, 'description')"
              @input="() => { if (touchedItems[index]?.description) validateField(index, 'description'); }"
            />
          </div>
          
          <!-- Tag selecionada -->
          <div v-if="item.tagId" class="flex items-center gap-2 pt-1">
              <span class="text-xs text-n-slate-11">Tag selecionada:</span>
              <span 
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                :style="{ 
                  backgroundColor: availableLabels.find(l => l.id === item.tagId)?.color || '#e5e7eb',
                  color: '#ffffff'
                }"
              >
                {{ getTagName(item.tagId) }}
              </span>
            </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
// Importar animações compartilhadas
@import '../shared/DynamicFieldAnimations.scss';

// Estilos específicos para tags
.tag-field-item {
  transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
  position: relative;
  z-index: 1;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    z-index: 100; // Z-index alto para o container em hover
  }

  // Quando o dropdown está aberto, aumenta ainda mais o z-index
  &:has(.custom-dropdown div[style*="z-index: 50"]) {
    z-index: 200 !important;
  }
}

// Garantir que o dropdown sempre fique acima
.custom-dropdown {
  position: relative;

  > div[style*="z-index: 50"] {
    z-index: 50 !important;
    position: absolute !important;
  }
}

// Animação do dropdown
.tag-dropdown {
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &.dropdown-open {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: var(--primary-600);
  }
}

// Usa a animação do arquivo compartilhado
// .remove-button-rotate está definido em DynamicFieldAnimations.scss

// Animação de seleção de tag
.tag-option {
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: translateX(4px);
  }
}

// Badge da tag com animação
.tag-badge {
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

// Animação ao abrir/fechar dropdown
.dropdown-list-enter-active,
.dropdown-list-leave-active {
  transition: all 0.2s ease;
}

.dropdown-list-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-list-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>